import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ShoppingCart, Trash2, CreditCard, X } from "lucide-react";

import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/hooks/useAuth";
import { useHasAllAccess } from "@/hooks/useHasAllAccess";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import AuthGateDialog from "./AuthGateDialog";
import CouponInput, { AppliedCoupon } from "./CouponInput";
import PurchaseChoiceDialog from "@/components/PurchaseChoiceDialog";
import { VideoThumbnailImage } from "@/components/video-library/VideoThumbnailImage";

interface CartDrawerProps {
  triggerVariant?: "icon" | "button";
}

const CartDrawer = ({ triggerVariant = "icon" }: CartDrawerProps) => {
  const { t } = useTranslation("common");
  const { cartItems, removeFromCart, clearCart, cartTotal, cartCount } = useCart();
  const { user } = useAuth();
  const { data: hasAllAccess } = useHasAllAccess(user?.id);
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  const [showAuthGate, setShowAuthGate] = useState(false);
  const [showChoice, setShowChoice] = useState(false);
  const [open, setOpen] = useState(false);
  const [coupon, setCoupon] = useState<AppliedCoupon | null>(null);

  const currency = cartItems[0]?.currency || "USD";

  // Detect mixed billing intervals (one-time + subscription, or month + year)
  const intervals = Array.from(new Set(cartItems.map((i) => i.billingInterval || "one_time")));
  const hasMixedIntervals = intervals.length > 1;

  const startCheckout = async (guestEmail?: string, giftRecipientEmail?: string) => {
    if (cartItems.length === 0) return;
    setIsProcessing(true);
    try {
      // If every item in the cart was added as a unit-buy, request unit pricing
      // (server forces the universal Single Video tier for each video).
      const allUnit = cartItems.every((i) => i.purchaseMode === "unit");
      const body: Record<string, any> = {
        video_ids: cartItems.map((i) => i.id),
        ...(allUnit ? { purchase_mode: "unit" } : {}),
        ...(coupon ? { coupon_code: coupon.code } : {}),
        ...(giftRecipientEmail ? { gift_recipient_email: giftRecipientEmail } : {}),
      };
      try { const { getStoredRef } = await import("@/lib/referralStorage"); const r = getStoredRef(); if (r) body.referral_code = r; } catch {}

      let data: any;
      if (guestEmail) {
        // Guest checkout: use raw fetch so the SDK does not inject a (possibly
        // stale / sub-less) Authorization header that the function would then
        // try to validate and reject with 401.
        body.guest_email = guestEmail;
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
        const response = await fetch(`${supabaseUrl}/functions/v1/create-video-checkout`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            apikey: anonKey,
          },
          body: JSON.stringify(body),
        });
        data = await response.json();
        if (!response.ok) throw new Error(data?.error || "Checkout failed");
      } else {
        const { data: sessionData } = await supabase.auth.getSession();
        if (!sessionData.session) throw new Error("No session");
        const { data: fnData, error } = await supabase.functions.invoke("create-video-checkout", {
          body,
          headers: { Authorization: `Bearer ${sessionData.session.access_token}` },
        });
        if (error) {
          const errorMsg = typeof fnData === "object" && (fnData as any)?.error ? (fnData as any).error : error.message;
          throw new Error(errorMsg);
        }
        data = fnData;
      }

      if (data?.error) throw new Error(data.error);
      if (data?.url) {
        window.open(data.url, "_blank");
        clearCart();
        setOpen(false);
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (error: any) {
      console.error("Cart checkout error:", error);
      toast({
        title: t("error"),
        description: error.message || t("videos_page.checkout_error", "Could not start checkout"),
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCheckout = () => {
    if (!user) {
      setShowAuthGate(true);
      return;
    }
    // For one-time video carts, only ask "for me or as a gift?" if the buyer
    // already has All-Access. For subscription carts, always ask.
    const isOneTimeCart = intervals[0] === "one_time" && !hasMixedIntervals;
    if (isOneTimeCart && !hasAllAccess) {
      startCheckout();
      return;
    }
    setShowChoice(true);
  };

  const handleGuestContinue = (email: string) => {
    setShowAuthGate(false);
    startCheckout(email);
  };

  const handleChoiceConfirmed = (mode: "self" | "gift", recipientEmail?: string) => {
    setShowChoice(false);
    startCheckout(undefined, mode === "gift" ? recipientEmail : undefined);
  };

  return (
    <>
      <Sheet open={open} onOpenChange={setOpen}>
        {triggerVariant === "button" ? (
          <SheetTrigger asChild>
            <Button className="w-full gap-2 bg-lemon-green hover:bg-lemon-green-dark text-white">
              <ShoppingCart className="h-4 w-4" />
              {t("videos_page.cart", "Cart")}
              {cartCount > 0 && (
                <span className="inline-flex items-center justify-center bg-white/20 text-white rounded-full min-w-[20px] h-[20px] px-1.5 text-[11px] font-bold leading-none">
                  {cartCount}
                </span>
              )}
            </Button>
          </SheetTrigger>
        ) : (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <SheetTrigger asChild>
                  <button
                    className="relative p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                    aria-label={t("videos_page.cart", "Cart")}
                  >
                    <ShoppingCart className="h-5 w-5 text-white" />
                    {cartCount > 0 && (
                      <span className="absolute -top-2 -right-2 flex items-center justify-center bg-emerald-500 text-white rounded-full min-w-[20px] h-[20px] px-1.5 text-[11px] font-bold leading-none shadow-sm">
                        {cartCount}
                      </span>
                    )}
                  </button>
                </SheetTrigger>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t("videos_page.cart", "Cart")}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        <SheetContent className="w-full sm:max-w-md flex flex-col">

          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5" />
              {t("videos_page.cart", "Shopping Cart")}
              {cartCount > 0 && (
                <Badge variant="secondary" className="ml-1">{cartCount}</Badge>
              )}
            </SheetTitle>
          </SheetHeader>

          {cartItems.length === 0 ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center space-y-2">
                <ShoppingCart className="h-12 w-12 mx-auto text-muted-foreground/30" />
                <p className="text-sm text-muted-foreground">
                  {t("videos_page.cart_empty", "Your cart is empty")}
                </p>
              </div>
            </div>
          ) : (
            <>
              <ScrollArea className="flex-1 -mx-6 px-6">
                <div className="space-y-3 py-4">
                  {cartItems.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center gap-3 rounded-lg border p-3"
                    >
                      {/* Thumbnail */}
                      <div className="w-16 h-10 rounded overflow-hidden shrink-0 bg-muted">
                        {item.thumbnail ? (
                          <VideoThumbnailImage src={item.thumbnail} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <video src={item.src} preload="metadata" muted className="w-full h-full object-cover pointer-events-none" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{item.title}</p>
                        <p className="text-xs text-muted-foreground">{item.priceLabel}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 shrink-0"
                        onClick={() => removeFromCart(item.id)}
                        aria-label={t("videos_page.remove_from_cart", { title: item.title })}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </ScrollArea>

              {/* Footer */}
              <div className="border-t pt-4 space-y-3">
                <CouponInput
                  context="videos"
                  amount={cartTotal}
                  applied={coupon}
                  onApply={setCoupon}
                  onRemove={() => setCoupon(null)}
                />
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t("videos_page.subtotal", "Subtotal")}</span>
                  <span>${cartTotal.toLocaleString()} {currency}</span>
                </div>
                {coupon && (
                  <div className="flex justify-between text-sm text-emerald-600">
                    <span>{t("videos_page.discount", "Discount")} ({coupon.code})</span>
                    <span>−${coupon.discount_amount.toLocaleString()} {currency}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-base">
                  <span>{t("videos_page.total", "Total")}</span>
                  <span className="text-primary">
                    ${(coupon ? coupon.final_amount : cartTotal).toLocaleString()} {currency}
                  </span>
                </div>
                {hasMixedIntervals && (
                  <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                    {t(
                      "videos_page.cart_mixed_intervals",
                      "Subscription items must be checked out separately from one-time purchases (and from each other)."
                    )}
                  </div>
                )}
                <Button
                  className="w-full"
                  size="lg"
                  onClick={handleCheckout}
                  disabled={isProcessing || hasMixedIntervals}
                >
                  <CreditCard className="mr-2 h-5 w-5" />
                  {isProcessing
                    ? t("videos_page.redirecting", "Redirecting to Stripe...")
                    : t("videos_page.checkout_all", "Checkout All")}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full text-xs"
                  onClick={clearCart}
                >
                  <Trash2 className="mr-1 h-3 w-3" />
                  {t("videos_page.clear_cart", "Clear Cart")}
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <AuthGateDialog
        open={showAuthGate}
        onOpenChange={setShowAuthGate}
        onGuestContinue={handleGuestContinue}
      />

      <PurchaseChoiceDialog
        open={showChoice}
        onOpenChange={setShowChoice}
        productType={intervals[0] === "one_time" ? "video" : "subscription"}
        loading={isProcessing}
        onConfirm={handleChoiceConfirmed}
      />
    </>
  );
};

export default CartDrawer;
